import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { api } from "../api/apiClient";
import { duplicatePaperFromError } from "../api/duplicatePaper";
import type { DuplicatePaperRef, PaperUse, Supplier } from "../api/types";
import { PaperUsePicker } from "../components/invoices/PaperUsePicker";
import { PAPER_USES, paperUseLabel, supplierDefaultPaperUse } from "../utils/paperUse";
import { useAuth } from "../state/AuthContext";
import type { DashboardOutletContext } from "./dashboardOutletContext";

export function InvoiceUploadPage() {
  const { user } = useAuth();
  const { locations } = useOutletContext<DashboardOutletContext>();
  const navigate = useNavigate();
  // G-127: the paper page's "Delete paper" lands here, saying what went.
  const deletedPaper = (useLocation().state as { deletedPaper?: string } | null)?.deletedPaper ?? null;
  const isOwner = user?.role === "OWNER";

  const [supplierId, setSupplierId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  // G-121 S4 (OD-143): what the paper is for — no default, the person picks;
  // OD-155: except a supplier with a known paper (Deco Spirit) is pre-selected
  // until the person picks something themselves.
  const [paperUse, setPaperUse] = useState<PaperUse | null>(null);
  const [paperUsePicked, setPaperUsePicked] = useState(false);
  const [duplicate, setDuplicate] = useState<DuplicatePaperRef | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .listSuppliers()
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, []);

  useEffect(() => {
    if (!locationId && locations.length) {
      setLocationId(locations[0].id);
    }
  }, [locations, locationId]);

  const suppliersSorted = useMemo(() => {
    const active = suppliers.filter((s) => s.isActive);
    return [...active].sort((a, b) => {
      const ap = a.hasParser ? 0 : 1;
      const bp = b.hasParser ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return a.name.localeCompare(b.name);
    });
  }, [suppliers]);

  const suppliersWithParser = useMemo(
    () => suppliersSorted.filter((s) => s.hasParser),
    [suppliersSorted],
  );

  const suppliersWithoutParser = useMemo(
    () => suppliersSorted.filter((s) => !s.hasParser),
    [suppliersSorted],
  );

  const selectedSupplier = useMemo(
    () => suppliersSorted.find((s) => s.id === supplierId),
    [suppliersSorted, supplierId],
  );

  const parserHint = useMemo(() => {
    if (!selectedSupplier) return null;
    if (selectedSupplier.hasParser) return null;
    return `The app has no reader for ${selectedSupplier.name} (${selectedSupplier.code}) yet — its PDF cannot be read. Pick a supplier under "Has PDF reader".`;
  }, [selectedSupplier]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setDuplicate(null);
    try {
      if (!supplierId) throw new Error("Pick a supplier");
      if (!locationId) throw new Error("Pick a shop");
      if (!paperUse) throw new Error("Say what this paper is");
      if (!file) throw new Error("Choose a PDF invoice");
      if (file.type && file.type !== "application/pdf") {
        throw new Error("Only PDF invoices are supported");
      }

      const created = await api.uploadInvoice({
        file,
        locationId,
        supplierId,
        paperUse,
      });
      navigate(`/app/invoices/${created.id}`);
    } catch (err: unknown) {
      const twin = duplicatePaperFromError(err);
      if (twin) {
        setDuplicate(twin);
        return;
      }
      setError(err instanceof Error ? err.message : "Could not upload invoice");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">Upload invoice</h2>
          <p className="page-subtitle">
            Upload a supplier PDF. The server parses it, then you link catalogue items and zones before
            scheduling the delivery.
          </p>
        </div>
        <Link to="/app/deliveries" className="button plain">
          Back to deliveries
        </Link>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {deletedPaper && !duplicate ? (
        <div className="alert success" role="status">
          Deleted {deletedPaper}. You can upload the same PDF again.
        </div>
      ) : null}
      {duplicate ? (
        <div className="alert warning" role="alert">
          {duplicate.canOpen ? (
            <>
              This exact file is already in the app: {paperUseLabel(duplicate.paperUse)}
              {duplicate.invoiceNumber ? ` ${duplicate.invoiceNumber}` : ""}, uploaded for{" "}
              {duplicate.locationName} on {duplicate.createdAt.slice(0, 10)}.{" "}
              <Link to={`/app/invoices/${duplicate.id}`} className="alert-link">
                Open it
              </Link> — its items were created when it was read; the owner can delete it
              to read it again, or change what that paper is there.
            </>
          ) : (
            "This exact file is already in the app, for a shop you cannot open. Ask the owner."
          )}
        </div>
      ) : null}
      {parserHint ? <div className="alert">{parserHint}</div> : null}

      <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <div className="field">
          <label htmlFor="invoice-supplier">Supplier</label>
          <select
            id="invoice-supplier"
            value={supplierId}
            onChange={(e) => {
              const id = e.target.value;
              setSupplierId(id);
              if (!paperUsePicked) {
                setPaperUse(supplierDefaultPaperUse(suppliersSorted.find((s) => s.id === id)?.code));
              }
            }}
          >
            <option value="">Select supplier…</option>
            {suppliersWithParser.length > 0 ? (
              <optgroup label="Has PDF reader">
                {suppliersWithParser.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {suppliersWithoutParser.length > 0 ? (
              <optgroup
                label={
                  suppliersWithParser.length > 0
                    ? "No PDF reader yet"
                    : "Suppliers (no PDF reader yet)"
                }
              >
                {suppliersWithoutParser.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </div>

        <div className="field">
          <label htmlFor="invoice-location">Shop</label>
          <select
            id="invoice-location"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            disabled={!isOwner && locations.length === 1}
          >
            <option value="">Select a shop</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
          {!isOwner ? (
            <small className="hint">Managers can only upload for their assigned shop.</small>
          ) : null}
        </div>

        <fieldset className="field paper-use-field">
          <legend className="field-caption">What is this paper?</legend>
          <PaperUsePicker
            name="invoice-paper-use"
            value={paperUse}
            options={PAPER_USES}
            onChange={(use) => {
              setPaperUse(use);
              setPaperUsePicked(true);
            }}
          />
          {!paperUsePicked && paperUse && paperUse === supplierDefaultPaperUse(selectedSupplier?.code) ? (
            <small className="hint">
              {selectedSupplier?.name} sends its factura with the goods, so this is pre-selected. Change it if
              this paper is something else.
            </small>
          ) : null}
        </fieldset>

        <div className="field">
          <label htmlFor="invoice-file">Invoice PDF</label>
          <input
            id="invoice-file"
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <button
            type="button"
            className="button"
            disabled={busy}
            onClick={() => void submit()}
          >
            {busy ? "Uploading…" : "Upload and parse"}
          </button>
        </div>
      </div>
    </section>
  );
}
